import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
  ACCOUNT_FIELD_CONFIGS,
  AccountFieldInput,
  useAccount,
} from '@hcs/authn';
import { Button, LoadingSpinner, NoContent } from '@hcs/design-system';
import { useOrgIsSsoOnly } from '@hcs/single-sign-on';
import { AccountFields } from '@hcs/types';

import { useUserChangePassword } from '../../hooks/useUserChangePassword';
import {
  ChangePasswordErrorData,
  ChangePasswordFormData,
} from '../../types/ChangePassword.types';

import styles from './UserChangePasswordForm.module.css';

const FORM_SCHEMA = yup.object().shape({
  [AccountFields.CurrentPassword]:
    ACCOUNT_FIELD_CONFIGS[AccountFields.CurrentPassword].validator,
  [AccountFields.NewPassword]:
    ACCOUNT_FIELD_CONFIGS[AccountFields.NewPassword].validator,
  [AccountFields.ConfirmNewPassword]:
    ACCOUNT_FIELD_CONFIGS[AccountFields.ConfirmNewPassword].validator,
});

interface Props {
  className?: string;
}
const dataHcName = 'user-change-password-form';
export const UserChangePasswordForm = ({ className }: Props) => {
  const { data: account } = useAccount();
  const { isFetched: ssoStateKnown, data: isSsoOnly } = useOrgIsSsoOnly(
    account?.organization.id,
  );
  const form = useForm<ChangePasswordFormData>({
    resolver: yupResolver(FORM_SCHEMA),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const { mutate: changeUserPassword } = useUserChangePassword({
    onError: (error) => {
      const errors = error.response?.data.errors;
      for (const formFieldStr in errors) {
        const formField =
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          formFieldStr as keyof ChangePasswordErrorData['errors'];
        const fieldError = errors[formField];
        if (fieldError) {
          form.setError(
            formField,
            {
              type: 'manual',
              message: fieldError,
            },
            { shouldFocus: true },
          );
        }
      }
    },
    onSuccess: () => form.reset(),
  });

  if (!ssoStateKnown || !account) {
    return <LoadingSpinner absoluteCenter />;
  }

  if (isSsoOnly)
    return (
      <NoContent>Your Organization requires login via Single Sign on</NoContent>
    );

  const handleSubmit = (changePasswordPayload: ChangePasswordFormData) => {
    changeUserPassword({
      userId: account.user.id,
      ...changePasswordPayload,
    });
  };

  return (
    <FormProvider {...form}>
      <form
        data-hc-name={dataHcName}
        className={className}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <Controller
          name={AccountFields.CurrentPassword}
          control={form.control}
          render={({ field }) => {
            return (
              <AccountFieldInput
                {...field}
                className={styles.Input}
                error={form.formState.errors[field.name]?.message}
              />
            );
          }}
        />
        <Controller
          name={AccountFields.NewPassword}
          control={form.control}
          render={({ field }) => {
            return (
              <AccountFieldInput
                {...field}
                className={styles.Input}
                error={form.formState.errors[field.name]?.message}
              />
            );
          }}
        />
        <Controller
          name={AccountFields.ConfirmNewPassword}
          control={form.control}
          render={({ field }) => {
            return (
              <AccountFieldInput
                {...field}
                className={styles.Input}
                error={form.formState.errors[field.name]?.message}
              />
            );
          }}
        />
        <Button
          dataHcName={`${dataHcName}-submit`}
          type="submit"
          label="Submit"
          disabled={!form.formState.isValid}
        />
      </form>
    </FormProvider>
  );
};
